import { useCallback, useState } from 'react';
import ResultModal from '@/components/common/elements/ResultModal';
import { getUserFacingErrorMessage } from '@/utils/errorMessage';

interface ResultState {
  open: boolean;
  status: boolean;
  title: string;
  message: string;
  onConfirm?: () => void;
}

/**
 * ResultModal 기반 결과/에러 알림 훅. 시스템 alert() 대체용.
 * - showError(error): 에러 정책([getUserFacingErrorMessage])에 따라 안전한 문구로 노출(미정의 에러는 일반 문구)
 * - showErrorMessage(msg): 고정 실패 문구 노출
 * - showSuccess(msg): 성공(체크) 노출
 * 사용처에서 반환된 `resultModal`을 렌더 트리에 포함시킨다.
 */
export function useResultModal() {
  const [state, setState] = useState<ResultState>({ open: false, status: true, title: '', message: '' });

  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  // 확인 버튼이든 X/배경 클릭이든 어느 경로로 닫혀도 onConfirm(삭제 후 이동 등)을 실행한다.
  // setState updater 내부에서 콜백을 직접 호출하면 '렌더 중 다른 컴포넌트 상태변경' 경고 소지가 있어
  // queueMicrotask로 지연 실행한다.
  const dismiss = useCallback(() => {
    setState((s) => {
      if (s.onConfirm) queueMicrotask(s.onConfirm);
      return { ...s, open: false };
    });
  }, []);

  const showError = useCallback((error: unknown, title = '오류', onConfirm?: () => void) => {
    setState({ open: true, status: false, title, message: getUserFacingErrorMessage(error), onConfirm });
  }, []);

  const showErrorMessage = useCallback((message: string, title = '오류', onConfirm?: () => void) => {
    setState({ open: true, status: false, title, message, onConfirm });
  }, []);

  const showSuccess = useCallback((message: string, title = '완료', onConfirm?: () => void) => {
    setState({ open: true, status: true, title, message, onConfirm });
  }, []);

  const resultModal = (
    <ResultModal
      open={state.open}
      status={state.status}
      title={state.title}
      description={state.message}
      buttontext="확인"
      onClose={dismiss}
      onClick={dismiss}
    />
  );

  return { showError, showErrorMessage, showSuccess, closeResult: close, resultModal };
}
